import React, { useState, useEffect, useCallback } from 'react';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../../utils/logger';
import {
  SENDER_PERSONAS,
  PreviewStep,
  ComposeStep,
  ConfirmStep,
  DoneStep,
  ModalShell,
} from './EmailSegmentSteps';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ELIGIBLE_ROLES = new Set(['owner', 'admin', 'support']);

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

// ─────────────────────────────────────────────
// Main exported component
// ─────────────────────────────────────────────
export const EmailSegmentPanel = ({ filters }) => {
  const [role, setRole] = useState(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('preview'); // preview | compose | confirm | done
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [senderPersona, setSenderPersona] = useState('hello');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [createdCampaignId, setCreatedCampaignId] = useState(null);

  // Detect role once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/me`, { headers: authHeaders() });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setRole((data.role || '').toLowerCase());
      } catch (e) {
        logger.warn('[EmailSegmentPanel] failed to fetch admin role', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/campaigns/preview`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ filters: filters || {} }),
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(`Preview failed (${r.status}): ${txt}`);
      }
      const data = await r.json();
      setPreview(data);
    } catch (e) {
      setPreviewError(e.message || 'Failed to load audience preview');
      logger.error('[EmailSegmentPanel] preview error', e);
    } finally {
      setPreviewLoading(false);
    }
  }, [filters]);

  const openPanel = () => {
    setOpen(true);
    setStep('preview');
    setPreview(null);
    setPreviewError(null);
    setSubject('');
    setBody('');
    setConfirmText('');
    setCreatedCampaignId(null);
    fetchPreview();
  };

  const closePanel = () => {
    if (sending) return; // don't close mid-send
    setOpen(false);
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/campaigns/send-test`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ subject, body_text: body, sender_persona: senderPersona }),
      });
      if (!r.ok) {
        if (r.status === 502) {
          throw new Error("Couldn't reach the email provider. Check that Resend is configured and the sender persona is verified.");
        }
        if (r.status === 403) {
          throw new Error("You don't have permission to send test emails.");
        }
        if (r.status === 422 || r.status === 400) {
          throw new Error('Subject or message is invalid. Please review and try again.');
        }
        throw new Error(`Test send failed (${r.status}). Please try again.`);
      }
      const data = await r.json();
      toast.success(`Test sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e.message || 'Test send failed');
      logger.error('[EmailSegmentPanel] test send error', e);
    } finally {
      setSendingTest(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/campaigns`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          subject,
          body_text: body,
          sender_persona: senderPersona,
          filters: preview?.filters_snapshot || filters || {},
        }),
      });
      if (!r.ok) {
        if (r.status === 409) {
          throw new Error('Another campaign is currently sending. Please wait for it to finish or cancel it.');
        }
        if (r.status === 400) {
          throw new Error('Audience is empty after applying filters and the unsubscribe list.');
        }
        if (r.status === 403) {
          throw new Error("You don't have permission to send campaigns.");
        }
        throw new Error(`Send failed (${r.status}). Please try again.`);
      }
      const data = await r.json();
      setCreatedCampaignId(data.campaign_id);
      setStep('done');
      toast.success(`Campaign queued — ${data.audience_count} ${data.audience_count === 1 ? 'email' : 'emails'} will send shortly`);
    } catch (e) {
      toast.error(e.message || 'Campaign send failed');
      logger.error('[EmailSegmentPanel] send error', e);
    } finally {
      setSending(false);
    }
  };

  if (role === null) return null; // still loading
  if (!ELIGIBLE_ROLES.has(role)) return null; // not authorized

  const senderLabel = SENDER_PERSONAS.find((p) => p.key === senderPersona)?.label || senderPersona;

  return (
    <>
      <button
        onClick={openPanel}
        className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg transition"
        data-testid="email-segment-open-btn"
      >
        <Mail className="w-4 h-4" />
        Email this segment
      </button>

      {open && (
        <ModalShell
          onClose={closePanel}
          title={
            step === 'preview' ? 'Email this segment — preview audience'
              : step === 'compose' ? 'Write your message'
              : step === 'confirm' ? 'Confirm send'
              : 'Done'
          }
        >
          {step === 'preview' && (
            <PreviewStep
              preview={preview}
              loading={previewLoading}
              error={previewError}
              onCancel={closePanel}
              onContinue={() => setStep('compose')}
              senderPersona={senderPersona}
              setSenderPersona={setSenderPersona}
            />
          )}
          {step === 'compose' && (
            <ComposeStep
              subject={subject}
              setSubject={setSubject}
              body={body}
              setBody={setBody}
              sample={preview?.sample_recipients?.[0]}
              onBack={() => setStep('preview')}
              onContinue={() => { setConfirmText(''); setStep('confirm'); }}
              onSendTest={handleSendTest}
              sendingTest={sendingTest}
            />
          )}
          {step === 'confirm' && (
            <ConfirmStep
              subject={subject}
              audienceCount={preview?.audience_count || 0}
              senderPersonaLabel={senderLabel}
              onBack={() => setStep('compose')}
              onSend={handleSend}
              sending={sending}
              confirmText={confirmText}
              setConfirmText={setConfirmText}
            />
          )}
          {step === 'done' && (
            <DoneStep
              campaignId={createdCampaignId}
              audienceCount={preview?.audience_count || 0}
              onClose={closePanel}
            />
          )}
        </ModalShell>
      )}
    </>
  );
};
